// pages/BrokerDashboard.js — rebuilt 30 July 2026
//
// This is the first screen after login, so it is built around "what do I do
// now", not "here are some totals". The order is deliberate:
//   1. Since you were last here  — what changed while you were away
//   2. Needs your attention      — one ranked list, not four scattered banners
//   3. Snapshot                  — four numbers, same definitions as /analytics
//   4. Work queue + pipeline     — the actual rows to act on
//   5. Recent activity           — real work, from the domain tables
//
// It deliberately does NOT duplicate /analytics: no source ROI, no team
// leaderboard, no trends. This page is about *now*; that page is about *how
// we're doing*. Every deeper question links through.
//
// All data comes from ONE call to /api/broker/dashboard. The old page made six,
// two of which pulled entire lists purely to call .length on them.
//
// CSS: every class is scoped under .bcd-dash. create-react-app compiles all
// imported CSS into one global sheet, so a bare class name here can be
// restyled by any other component's stylesheet — that is exactly how
// `.bc-row { display:flex }` in UserManager.css silently broke the analytics
// tables. Do not de-scope these, and do not reuse generic names like
// .loading / .page-header / .stat-box, each of which is defined in several
// unrelated stylesheets in this codebase.

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import Sidebar from '../components/Sidebar';
import SetupChecklist from '../components/SetupChecklist';
import { useMobile } from '../hooks/useMobile';

const token = () => localStorage.getItem('token');

/* ── palette: shared with the rebuilt /analytics page ─────────────────── */
const C = {
  ink: '#0e1b2c', ink70: '#4b5a6e', muted: '#7c8b9e', faint: '#a8b4c2',
  line: '#E6EBF1', bg: '#F5F7FA', card: '#ffffff',
  green: '#0ea472', greenBg: '#e7f7f1',
  indigo: '#5b62f4', indigoBg: '#eeeffe',
  amber: '#e08700', amberBg: '#fdf3e2',
  red: '#dc2b3f', redBg: '#fdeced',
  sky: '#0b8fd4', skyBg: '#e6f4fc',
};

const gbp  = (v) => '£' + Number(v || 0).toLocaleString('en-GB', { maximumFractionDigits: 0 });
const nInt = (v) => Number(v || 0).toLocaleString('en-GB');
const pct  = (v) => `${Number(v || 0).toFixed(Number(v) % 1 === 0 ? 0 : 1)}%`;

// "3 leads" / "1 lead" — the API sends "singular|plural" so the wording lives
// next to the query that produced the number.
const plural = (label, n) => {
  const [one, many] = String(label).split('|');
  return n === 1 ? one : (many || one);
};

const ago = (iso) => {
  if (!iso) return '';
  const secs = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (secs < 60)    return 'just now';
  if (secs < 3600)  return `${Math.floor(secs / 60)}m ago`;
  if (secs < 86400) return `${Math.floor(secs / 3600)}h ago`;
  const d = Math.floor(secs / 86400);
  if (d < 7) return `${d}d ago`;
  return new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
};

const greeting = () => {
  const h = new Date().getHours();
  if (h < 12) return 'Good morning';
  if (h < 18) return 'Good afternoon';
  return 'Good evening';
};

const ACTIVITY = {
  lead_in:        { icon: '📥', verb: 'lead received from' },
  deal_created:   { icon: '📋', verb: 'deal opened' },
  stage_change:   { icon: '🔄', verb: 'moved to' },
  deal_completed: { icon: '🏁', verb: 'completed' },
};

const TONE = {
  high:   { c: C.red,   bg: C.redBg,   b: '#f6c6cb' },
  medium: { c: C.amber, bg: C.amberBg, b: '#f3ddb4' },
  low:    { c: C.sky,   bg: C.skyBg,   b: '#c3e2f4' },
};

/* ── building blocks ──────────────────────────────────────────────────── */

function Card({ children, style, pad = 22 }) {
  return (
    <div style={{
      background: C.card, border: `1px solid ${C.line}`, borderRadius: 14, padding: pad,
      boxShadow: '0 1px 2px rgba(16,24,40,0.04), 0 1px 3px rgba(16,24,40,0.03)',
      minWidth: 0, ...style,
    }}>{children}</div>
  );
}

function CardHead({ title, hint, right }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start',
                  gap: 12, marginBottom: 14 }}>
      <div style={{ minWidth: 0 }}>
        <h2 style={{ margin: 0, fontSize: 15, fontWeight: 700, color: C.ink, letterSpacing: '-0.01em' }}>
          {title}
        </h2>
        {hint && <p style={{ margin: '3px 0 0', fontSize: 12.5, color: C.muted, lineHeight: 1.4 }}>{hint}</p>}
      </div>
      {right}
    </div>
  );
}

function Delta({ value }) {
  if (value === null || value === undefined) {
    return <span style={{ fontSize: 12, color: C.faint }}>no prior month</span>;
  }
  const flat   = Math.abs(value) < 0.05;
  const colour = flat ? C.muted : value > 0 ? C.green : C.red;
  const bg     = flat ? '#F1F4F8' : value > 0 ? C.greenBg : C.redBg;
  return (
    <span style={{ fontSize: 12, fontWeight: 600, color: colour, background: bg,
                   padding: '2px 7px', borderRadius: 20, whiteSpace: 'nowrap' }}>
      {flat ? '– 0%' : `${value > 0 ? '▲' : '▼'} ${Math.abs(value)}%`}
    </span>
  );
}

function Kpi({ label, value, sub, delta, accent = C.ink, tag, onClick }) {
  return (
    <Card pad={18} style={{ cursor: onClick ? 'pointer' : 'default' }}>
      <div onClick={onClick} style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <span style={{ fontSize: 11.5, fontWeight: 600, color: C.muted,
                         textTransform: 'uppercase', letterSpacing: '0.04em' }}>{label}</span>
          {tag && (
            <span style={{ fontSize: 9.5, fontWeight: 700, color: C.sky, background: C.skyBg,
                           padding: '1px 5px', borderRadius: 4, textTransform: 'uppercase',
                           letterSpacing: '0.04em' }}>{tag}</span>
          )}
        </div>
        <div style={{ fontSize: 27, fontWeight: 700, color: accent,
                      letterSpacing: '-0.02em', lineHeight: 1.1 }}>{value}</div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', minHeight: 20 }}>
          {delta !== undefined && <Delta value={delta} />}
          {sub && <span style={{ fontSize: 12, color: C.faint }}>{sub}</span>}
        </div>
      </div>
    </Card>
  );
}

function MiniBar({ value, max, colour }) {
  const w = max > 0 ? Math.max(value > 0 ? 3 : 0, (value / max) * 100) : 0;
  return (
    <div style={{ background: '#EEF1F5', borderRadius: 20, height: 6, width: '100%', overflow: 'hidden' }}>
      <div style={{ width: `${w}%`, height: '100%', background: colour, borderRadius: 20 }} />
    </div>
  );
}

function Empty({ icon, children }) {
  return (
    <div style={{ padding: '26px 8px', textAlign: 'center', color: C.faint, fontSize: 13.5 }}>
      {icon && <div style={{ fontSize: 24, marginBottom: 6, opacity: 0.6 }}>{icon}</div>}
      {children}
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════════════ */

/* ── Back Book summary — 14 Aug 2026 ──────────────────────────────────────
   Self-contained: fetches its own data from GET /api/backbook/summary rather
   than threading through the main dashboard load(), so it can be dropped in
   or removed by a single `{hasBackbook && <BackBookSummary .../>}` with no
   other wiring. Reuses the same Card/CardHead/Kpi/MiniBar building blocks as
   the rest of this page so it doesn't look bolted-on. ────────────────────── */
function BackBookSummary({ navigate, mobile }) {
  const [sum, setSum] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetch('/api/backbook/summary', { headers: { Authorization: `Bearer ${token()}` } })
      .then(r => r.ok ? r.json() : null)
      .then(setSum)
      .finally(() => setLoading(false));
  }, []);

  if (loading) return null; // no spinner of its own — the page's own load() already covers the wait
  const t = sum?.totals || {};
  const byAdviser = sum?.byAdviser || [];
  const maxClients = Math.max(1, ...byAdviser.map(a => a.client_count));
  const outcomes = sum?.recentOutcomes || [];
  const renewed = outcomes.find(o => o.outcome === 'renewed')?.count || 0;
  const lost    = outcomes.find(o => o.outcome === 'lost')?.count || 0;

  return (
    <>
      <div style={{ display: 'grid', gap: 14, marginBottom: 18,
                    gridTemplateColumns: mobile ? '1fr 1fr' : 'repeat(auto-fit, minmax(200px, 1fr))' }}>
        <Kpi label="Active clients" value={nInt(t.active_clients)} accent={C.ink}
             sub="in your back book" onClick={() => navigate('/backbook')} />
        <Kpi label="Overdue for renewal" value={nInt(t.overdue)} accent={t.overdue > 0 ? C.red : C.ink}
             sub="past their end date" onClick={() => navigate('/backbook')} />
        <Kpi label="Due within 3 months" value={nInt(t.due_3mo)} accent={C.amber}
             sub="the 8/6/3-month alerts cover these" onClick={() => navigate('/backbook')} />
        <Kpi label="Renewed vs lost" value={`${nInt(renewed)} / ${nInt(lost)}`} accent={C.green}
             sub="last 90 days" onClick={() => navigate('/backbook')} />
      </div>

      {byAdviser.length > 0 && (
        <Card style={{ marginBottom: 18 }}>
          <CardHead title="Back Book by adviser" hint="Who's carrying what — use this to keep allocation even." />
          {byAdviser.map(a => (
            <div key={a.adviser_id || 'unassigned'} className="bcd-item"
                 onClick={() => navigate(`/backbook?adviser_id=${a.adviser_id || 'unassigned'}`)}
                 style={{ padding: '10px 6px', borderRadius: 6 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline',
                            gap: 10, marginBottom: 5 }}>
                <span style={{ fontSize: 13, color: C.ink70, fontWeight: 500 }}>
                  {a.adviser_name || 'Unassigned'}
                </span>
                <span style={{ fontSize: 12.5, color: C.muted, whiteSpace: 'nowrap' }}>
                  <strong style={{ color: C.ink }}>{a.client_count}</strong> {a.client_count === 1 ? 'client' : 'clients'}
                  {a.due_3mo > 0 ? (
                    <span style={{ color: C.amber }}> · {a.due_3mo} due in 3mo</span>
                  ) : null}
                </span>
              </div>
              <MiniBar value={a.client_count} max={maxClients} colour={C.indigo} />
            </div>
          ))}
        </Card>
      )}
    </>
  );
}

export default function BrokerDashboard() {
  const navigate = useNavigate();
  const mobile   = useMobile();
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState('');
  const [user, setUser]       = useState({});
  // Which modules this company has switched on (14 Aug 2026 — "super back book"
  // account for Alegra). Same live-fetch pattern as Sidebar.js/ModuleGate.js: the
  // dashboard's own sections adapt to whatever's enabled rather than needing a
  // separate "dashboard mode" setting, so flipping a module back on later just works.
  const [navModules, setNavModules] = useState({ leads: true, deals: true, introducers: true, backbook: false, analytics: true });

  const load = useCallback(() => {
    setError('');
    fetch('/api/broker/dashboard', { headers: { Authorization: `Bearer ${token()}` } })
      .then(r => { if (!r.ok) throw new Error('http'); return r.json(); })
      .then(d => { if (d.error) throw new Error(d.error); setData(d); })
      .catch(() => setError('Could not load your dashboard. Please try again.'))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    setUser(JSON.parse(localStorage.getItem('user') || '{}'));
    load();
    fetch('/api/company-profile/nav-modules', { headers: { Authorization: `Bearer ${token()}` } })
      .then(r => r.ok ? r.json() : { nav_modules: {} })
      .then(d => setNavModules(prev => ({ ...prev, ...d.nav_modules })))
      .catch(() => {});
  }, [load]);

  const ml  = mobile ? 0 : 70;
  const pt  = mobile ? 64 : 0;
  const pad = mobile ? 14 : 32;

  if (loading) {
    return (
      <div style={{ display: 'flex', minHeight: '100vh', background: C.bg }}>
        <Sidebar />
        <div style={{ flex: 1, marginLeft: ml, paddingTop: pt, display: 'flex',
                      alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ textAlign: 'center', color: C.muted }}>
            <div style={{ width: 30, height: 30, border: `3px solid ${C.line}`, borderTopColor: C.indigo,
                          borderRadius: '50%', margin: '0 auto 12px',
                          animation: 'bcdspin .8s linear infinite' }} />
            <div style={{ fontSize: 13.5 }}>Getting your day together…</div>
          </div>
          <style>{`@keyframes bcdspin{to{transform:rotate(360deg)}}`}</style>
        </div>
      </div>
    );
  }

  const s        = data?.snapshot || {};
  const att      = data?.attention || [];
  const stages   = data?.pipeline?.stages || [];
  const maxStage = Math.max(1, ...stages.map(x => x.deals));
  const qLeads   = data?.queue?.leads || [];
  const qActions = data?.queue?.actions || [];
  const stalled  = data?.queue?.stalled || [];
  const activity = data?.activity || [];
  const since    = data?.since;
  const firstName = (user?.name || '').trim().split(/\s+/)[0] || 'there';

  // Whether this account has anything pipeline-shaped switched on at all — controls
  // the whole "Since you were last here / Needs attention / Snapshot / Work queue /
  // Pipeline" half of this page, which is meaningless once Deals+Leads are both off.
  const hasPipeline     = navModules.deals !== false || navModules.leads !== false;
  const hasIntroducers  = navModules.introducers !== false;
  const hasAnalytics    = navModules.analytics !== false;
  const hasBackbook     = !!navModules.backbook;

  const QUICK = [
    // 4 Sep 2026: was plain '/backbook' - Adam Green (Alegra) reported this quick action
    // just dumps you on the list page instead of actually starting an add, same
    // ?action=add-on-mount deep-link pattern as the existing ?client=/?adviser_id= ones.
    ...(hasBackbook ? [{ label: '+ Add Back Book client', to: '/backbook?action=add', primary: !hasPipeline }] : []),
    ...(navModules.deals !== false ? [{ label: '+ New deal', to: '/create-deal', primary: true }] : []),
    ...(navModules.leads !== false ? [{ label: '+ Add lead', to: '/leads' }] : []),
    ...(hasIntroducers ? [{ label: '+ Invite introducer', to: '/introducers' }] : []),
    ...(hasAnalytics ? [{ label: 'Analytics', to: '/analytics' }] : []),
  ];

  return (
    <div className="bcd-dash" style={{ display: 'flex', minHeight: '100vh', background: C.bg }}>
      <Sidebar />
      <style>{`
        @keyframes bcdspin{to{transform:rotate(360deg)}}
        .bcd-dash .bcd-btn{cursor:pointer;transition:all .15s ease}
        .bcd-dash .bcd-btn:hover{filter:brightness(1.06)}
        .bcd-dash .bcd-tile{cursor:pointer;transition:transform .12s ease, box-shadow .12s ease}
        .bcd-dash .bcd-tile:hover{transform:translateY(-1px);box-shadow:0 4px 14px rgba(16,24,40,.09)}
        .bcd-dash .bcd-item{cursor:pointer;transition:background .12s ease}
        .bcd-dash .bcd-item:hover{background:#FAFBFD}
      `}</style>

      <div style={{ flex: 1, marginLeft: ml, paddingTop: pt, minWidth: 0 }}>

        {/* ── Header ─────────────────────────────────────────────────── */}
        <div style={{ background: C.card, borderBottom: `1px solid ${C.line}`, padding: `20px ${pad}px` }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start',
                        gap: 14, flexWrap: 'wrap' }}>
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 9, flexWrap: 'wrap' }}>
                <h1 style={{ margin: 0, fontSize: mobile ? 20 : 25, fontWeight: 700,
                             color: C.ink, letterSpacing: '-0.02em' }}>
                  {greeting()}, {firstName}
                </h1>
                <span style={{ fontSize: 11, fontWeight: 700, color: C.indigo, background: C.indigoBg,
                               padding: '3px 9px', borderRadius: 20, textTransform: 'uppercase',
                               letterSpacing: '0.04em' }}>{data?.scopeLabel}</span>
              </div>
              <p style={{ margin: '4px 0 0', fontSize: 13, color: C.muted }}>
                {new Date().toLocaleDateString('en-GB', {
                  weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}
                {since?.lastSeenAt ? ` · last here ${ago(since.lastSeenAt)}` : ''}
              </p>
            </div>

            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              {QUICK.map(q => (
                <button key={q.label} className="bcd-btn" onClick={() => navigate(q.to)}
                  style={{
                    background: q.primary ? C.ink : '#fff',
                    color: q.primary ? '#fff' : C.ink70,
                    border: `1px solid ${q.primary ? C.ink : C.line}`,
                    borderRadius: 9, padding: '8px 14px', fontSize: 13, fontWeight: 600,
                  }}>{q.label}</button>
              ))}
            </div>
          </div>
        </div>

        <div style={{ padding: `${mobile ? 16 : 24}px ${pad}px 60px`, maxWidth: 1360 }}>

          <SetupChecklist token={token()} compact />

          {error && (
            <div style={{ background: C.redBg, border: '1px solid #f6c6cb', color: C.red,
                          borderRadius: 10, padding: '11px 15px', fontSize: 13.5, marginBottom: 18 }}>
              {error}
            </div>
          )}

          {/* ── Since you were last here ────────────────────────────── */}
          {hasPipeline && since && since.anything && (
            <Card pad={16} style={{ marginBottom: 18, background: C.indigoBg, borderColor: '#d9dcfd' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 18, flexWrap: 'wrap' }}>
                <span style={{ fontSize: 13, fontWeight: 700, color: C.ink }}>While you were away</span>
                {[
                  ['📥', since.newLeads,       'new lead',       'new leads'],
                  ['📋', since.dealsCreated,   'deal opened',    'deals opened'],
                  ['🔄', since.stageChanges,   'stage change',   'stage changes'],
                  ['🏁', since.dealsCompleted, 'deal completed', 'deals completed'],
                ].filter(x => x[1] > 0).map(x => (
                  <span key={x[2]} style={{ fontSize: 13, color: C.ink70 }}>
                    {x[0]} <strong style={{ color: C.ink }}>{nInt(x[1])}</strong> {x[1] === 1 ? x[2] : x[3]}
                  </span>
                ))}
              </div>
            </Card>
          )}

          {/* ── Needs your attention ────────────────────────────────── */}
          {(hasPipeline || att.length > 0) && (
          <Card style={{ marginBottom: 18 }}>
            <CardHead
              title="Needs your attention"
              hint={att.length ? 'Ranked by urgency. Everything here is waiting on someone.' : undefined}
              right={att.length > 0 ? (
                <span style={{ fontSize: 12, fontWeight: 700, color: TONE[att[0].severity].c,
                               background: TONE[att[0].severity].bg, padding: '3px 10px', borderRadius: 20 }}>
                  {att.length} {att.length === 1 ? 'thing' : 'things'}
                </span>
              ) : null}
            />
            {att.length === 0 ? (
              <Empty icon="✅">Nothing needs chasing — you're all clear.</Empty>
            ) : (
              <div style={{ display: 'grid', gap: 9,
                            gridTemplateColumns: mobile ? '1fr' : 'repeat(auto-fit, minmax(285px, 1fr))' }}>
                {att.map(a => {
                  const t = TONE[a.severity] || TONE.low;
                  return (
                    <div key={a.key} className="bcd-tile" onClick={() => navigate(a.link)}
                      style={{ display: 'flex', alignItems: 'center', gap: 12,
                               background: t.bg, border: `1px solid ${t.b}`,
                               borderRadius: 11, padding: '12px 14px' }}>
                      <span style={{ fontSize: 21, fontWeight: 700, color: t.c, lineHeight: 1,
                                     minWidth: 24, textAlign: 'center' }}>{a.count}</span>
                      <span style={{ fontSize: 13, color: C.ink70, lineHeight: 1.35, flex: 1 }}>
                        {plural(a.label, a.count)}
                      </span>
                      <span style={{ fontSize: 12, fontWeight: 700, color: t.c, whiteSpace: 'nowrap' }}>
                        {a.action} →
                      </span>
                    </div>
                  );
                })}
              </div>
            )}
          </Card>
          )}

          {/* ── Back Book summary ─────────────────────────────────── */}
          {hasBackbook && <BackBookSummary navigate={navigate} mobile={mobile} />}

          {/* ── Snapshot ───────────────────────────────────────────── */}
          {hasPipeline && (<>
          <div style={{ display: 'grid', gap: 14, marginBottom: 18,
                        gridTemplateColumns: mobile ? '1fr 1fr' : 'repeat(auto-fit, minmax(200px, 1fr))' }}>
            <Kpi label="Open pipeline" value={gbp(s.openPipeline)} tag="live" accent={C.amber}
                 sub={`${nInt(s.openDeals)} open ${s.openDeals === 1 ? 'deal' : 'deals'}`}
                 onClick={() => navigate('/deals')} />
            <Kpi label="Banked this month" value={gbp(s.bankedThisMonth)} accent={C.green}
                 delta={s.bankedDelta} sub={`${nInt(s.completedThisMonth)} completed`}
                 onClick={() => navigate('/analytics')} />
            <Kpi label="Leads this month" value={nInt(s.leadsThisMonth)} accent={C.indigo}
                 delta={s.leadsDelta} sub={`${nInt(s.openLeads)} still open`}
                 onClick={() => navigate('/leads')} />
            <Kpi label="Conversion" value={pct(s.conversionThisMonth)} accent={C.sky}
                 sub={`${nInt(s.convertedThisMonth)} of ${nInt(s.leadsThisMonth)} this month`}
                 onClick={() => navigate('/analytics')} />
          </div>

          {/* ── Work queue + pipeline ──────────────────────────────── */}
          <div style={{ display: 'grid', gap: 16, marginBottom: 18,
                        gridTemplateColumns: mobile ? '1fr' : 'minmax(0,1.3fr) minmax(0,1fr)' }}>

            <Card>
              <CardHead
                title="Your work queue"
                hint="Unassigned and oldest first — the leads most at risk of going cold."
                right={
                  <button className="bcd-btn" onClick={() => navigate('/leads')}
                    style={{ background: 'none', border: 'none', color: C.indigo,
                             fontSize: 12.5, fontWeight: 700, padding: 0 }}>All leads →</button>
                }
              />
              {qLeads.length === 0 ? (
                <Empty icon="🎉">No open leads waiting.</Empty>
              ) : (
                <div>
                  {qLeads.map((l, i) => (
                    <div key={l.id} className="bcd-item" onClick={() => navigate('/leads')}
                      style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '11px 6px',
                               borderTop: i === 0 ? 'none' : `1px solid ${C.line}`, borderRadius: 6 }}>
                      <span style={{ fontSize: 15, flexShrink: 0 }}>
                        {l.family === 'introducer' ? '🤝' : '🌐'}
                      </span>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: 13.5, fontWeight: 600, color: C.ink, overflow: 'hidden',
                                      textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{l.clientName}</div>
                        <div style={{ fontSize: 11.5, color: C.faint }}>
                          {l.sourceLabel}{l.assignedName ? ` · ${l.assignedName}` : ''}
                        </div>
                      </div>
                      {l.unassigned && (
                        <span style={{ fontSize: 10.5, fontWeight: 700, color: C.red, background: C.redBg,
                                       padding: '2px 7px', borderRadius: 20, whiteSpace: 'nowrap' }}>
                          Unassigned
                        </span>
                      )}
                      <span style={{ fontSize: 12, whiteSpace: 'nowrap', flexShrink: 0,
                                     color: l.ageDays > 14 ? C.red : l.ageDays > 7 ? C.amber : C.faint }}>
                        {l.ageDays === 0 ? 'today' : `${l.ageDays}d old`}
                      </span>
                    </div>
                  ))}
                </div>
              )}

              {qActions.length > 0 && (
                <div style={{ marginTop: 16, paddingTop: 14, borderTop: `1px solid ${C.line}` }}>
                  <div style={{ fontSize: 11.5, fontWeight: 600, color: C.muted, textTransform: 'uppercase',
                                letterSpacing: '0.04em', marginBottom: 8 }}>Actions on deals</div>
                  {qActions.map(a => (
                    <div key={a.id} className="bcd-item" onClick={() => navigate(`/deal/${a.dealId}`)}
                      style={{ display: 'flex', alignItems: 'center', gap: 10,
                               padding: '9px 6px', borderRadius: 6 }}>
                      <span style={{ width: 8, height: 8, borderRadius: '50%', flexShrink: 0,
                                     background: a.overdue ? C.red : C.amber }} />
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: 13, fontWeight: 600, color: C.ink, overflow: 'hidden',
                                      textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{a.title}</div>
                        <div style={{ fontSize: 11.5, color: C.faint }}>
                          {a.clientName}{a.reference ? ` · ${a.reference}` : ''}
                        </div>
                      </div>
                      <span style={{ fontSize: 11.5, fontWeight: 700, whiteSpace: 'nowrap',
                                     color: a.overdue ? C.red : C.amber }}>
                        {a.overdue ? 'Overdue'
                          : new Date(a.dueDate).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </Card>

            <Card>
              <CardHead
                title="Pipeline"
                hint="Open deals by stage, with the longest wait in each."
                right={<span style={{ fontSize: 12.5, color: C.muted, whiteSpace: 'nowrap' }}>
                  <strong style={{ color: C.ink }}>{gbp(data?.pipeline?.openValue)}</strong>
                </span>}
              />
              {data?.pipeline?.openDeals === 0 ? (
                <Empty icon="📭">No open deals yet.</Empty>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                  {stages.map(st => (
                    <div key={st.name} className="bcd-item" onClick={() => navigate('/deals')}
                         style={{ borderRadius: 6, padding: '2px 4px' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between',
                                    alignItems: 'baseline', gap: 10, marginBottom: 5 }}>
                        <span style={{ fontSize: 13, color: C.ink70, fontWeight: 500 }}>{st.name}</span>
                        <span style={{ fontSize: 12.5, color: C.muted, whiteSpace: 'nowrap' }}>
                          <strong style={{ color: C.ink }}>{st.deals}</strong>
                          {st.value > 0 ? ` · ${gbp(st.value)}` : ''}
                          {st.deals > 0 && st.maxAgeDays > 0 ? (
                            <span style={{ color: st.maxAgeDays > 30 ? C.red
                                         : st.maxAgeDays > 14 ? C.amber : C.faint }}>
                              {' '}· {st.maxAgeDays}d
                            </span>
                          ) : null}
                        </span>
                      </div>
                      <MiniBar value={st.deals} max={maxStage}
                        colour={st.maxAgeDays > 30 ? C.red : st.maxAgeDays > 14 ? C.amber : C.indigo} />
                    </div>
                  ))}
                </div>
              )}

              {stalled.length > 0 && (
                <div style={{ marginTop: 16, paddingTop: 14, borderTop: `1px solid ${C.line}` }}>
                  <div style={{ fontSize: 11.5, fontWeight: 600, color: C.muted, textTransform: 'uppercase',
                                letterSpacing: '0.04em', marginBottom: 8 }}>Sitting longest</div>
                  {stalled.map(d => (
                    <div key={d.id} className="bcd-item" onClick={() => navigate(`/deal/${d.id}`)}
                      style={{ display: 'flex', alignItems: 'center', gap: 10,
                               padding: '8px 6px', borderRadius: 6 }}>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: 13, fontWeight: 600, color: C.ink, overflow: 'hidden',
                                      textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{d.clientName}</div>
                        <div style={{ fontSize: 11.5, color: C.faint }}>{d.stageName}</div>
                      </div>
                      <span style={{ fontSize: 12, fontWeight: 700, whiteSpace: 'nowrap',
                                     color: d.idleDays > 30 ? C.red : C.amber }}>{d.idleDays}d</span>
                    </div>
                  ))}
                </div>
              )}
            </Card>
          </div>
          </>)}

          {/* ── Activity + network ─────────────────────────────────── */}
          <div style={{ display: 'grid', gap: 16,
                        gridTemplateColumns: (mobile || !hasPipeline) ? '1fr' : 'minmax(0,1.3fr) minmax(0,1fr)' }}>

            {hasPipeline && (
            <Card>
              <CardHead title="Recent activity" hint="Leads in, deals opened, stages moved — last 30 days." />
              {activity.length === 0 ? (
                <Empty icon="🕓">Nothing has happened in the last 30 days.</Empty>
              ) : (
                <div>
                  {activity.map((a, i) => {
                    const meta = ACTIVITY[a.kind] || { icon: '📌', verb: '' };
                    const detail = a.kind === 'deal_completed' ? gbp(a.detail) : a.detail;
                    return (
                      <div key={a.id}
                        className={a.dealId ? 'bcd-item' : undefined}
                        onClick={a.dealId ? () => navigate(`/deal/${a.dealId}`) : undefined}
                        style={{ display: 'flex', alignItems: 'flex-start', gap: 11, padding: '10px 6px',
                                 borderTop: i === 0 ? 'none' : `1px solid ${C.line}`, borderRadius: 6 }}>
                        <span style={{ fontSize: 14, marginTop: 1, flexShrink: 0 }}>{meta.icon}</span>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontSize: 13, color: C.ink70 }}>
                            <strong style={{ color: C.ink }}>{a.subject}</strong>
                            {' — '}{meta.verb}{detail ? ` ${detail}` : ''}
                          </div>
                          {a.actor ? <div style={{ fontSize: 11.5, color: C.faint }}>{a.actor}</div> : null}
                        </div>
                        <span style={{ fontSize: 11.5, color: C.faint, whiteSpace: 'nowrap', flexShrink: 0 }}>
                          {ago(a.at)}
                        </span>
                      </div>
                    );
                  })}
                </div>
              )}
            </Card>
            )}

            <Card>
              <CardHead title="Your network" hint="Who you have on the books right now." />
              {[
                { label: 'Team members',        value: data?.roster?.teamActive,
                  link: '/team',        sub: 'active brokers and owners' },
                ...(hasIntroducers ? [
                  { label: 'Introducers',         value: data?.roster?.introducersActive,
                    link: '/introducers', sub: 'signed up and active' },
                  { label: 'Invites outstanding', value: data?.roster?.introducerInvitesPending,
                    link: '/introducers', sub: 'sent, not yet accepted' },
                ] : []),
              ].map(r => (
                <div key={r.label} className="bcd-item" onClick={() => navigate(r.link)}
                  style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                           padding: '13px 6px', borderBottom: `1px solid ${C.line}`, borderRadius: 6 }}>
                  <div>
                    <div style={{ fontSize: 13.5, color: C.ink70, fontWeight: 500 }}>{r.label}</div>
                    <div style={{ fontSize: 11.5, color: C.faint }}>{r.sub}</div>
                  </div>
                  <span style={{ fontSize: 20, fontWeight: 700,
                                 color: r.value ? C.ink : C.faint }}>{nInt(r.value)}</span>
                </div>
              ))}
              {hasAnalytics && (
              <button className="bcd-btn" onClick={() => navigate('/analytics')}
                style={{ marginTop: 16, width: '100%', background: '#fff', color: C.ink70,
                         border: `1px solid ${C.line}`, borderRadius: 9, padding: '10px 14px',
                         fontSize: 13, fontWeight: 600 }}>
                See full analytics →
              </button>
              )}
            </Card>
          </div>

          {hasPipeline && (
          <p style={{ marginTop: 22, fontSize: 12, color: C.faint, lineHeight: 1.6, maxWidth: 780 }}>
            <strong style={{ color: C.muted }}>How these are counted.</strong>{' '}
            Open pipeline is a live snapshot of open deals, valued at lender proc fee plus client fee.
            Banked counts completed deals only, dated by completion. Conversion is this month's leads
            that became a deal. Archived leads and deals are excluded throughout — the same
            definitions the Analytics page uses.
          </p>
          )}

        </div>
      </div>
    </div>
  );
}
